import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@pagespeedify/ui';
import { toast } from 'sonner';

// --- Interfaces ---
interface ContributingIssue {
  id: string;
  description: string;
  details?: string;
  effort?: number;
  savings?: number;
  numericUnit?: 'ms' | 's' | 'byte' | 'KB' | 'count' | '%';
  affectedResources?: Array<{ url: string;[key: string]: any }>;
  auditId?: string;
}

interface MetricData {
  score: number;
  name: string;
  title: string;
  definition: string;
  tooltipSuggestion?: string;
  contributingIssues?: ContributingIssue[];
}

interface InsightMatrixProps {
  pleiData?: MetricData;
  crdsData?: MetricData;
  oeeiData?: MetricData;
  ualiData?: MetricData;
  ilriData?: MetricData;
  rosrData?: MetricData;
}

const getSeverityClasses = (score: number, metricName: string): { bg: string; text: string; isGood: boolean } => {
  const lowerIsBetter = ['CRDS', 'UALI', 'ILRI', 'ROSR'].includes(metricName);
  if (lowerIsBetter) {
    if (score <= 20) return { bg: 'bg-green-500 dark:bg-green-600', text: 'text-white', isGood: true };
    if (score <= 50) return { bg: 'bg-yellow-500 dark:bg-yellow-600', text: 'text-white', isGood: false };
    return { bg: 'bg-red-500 dark:bg-red-600', text: 'text-white', isGood: false };
  } else {
    if (score >= 80) return { bg: 'bg-green-500 dark:bg-green-600', text: 'text-white', isGood: true };
    if (score >= 50) return { bg: 'bg-yellow-500 dark:bg-yellow-600', text: 'text-white', isGood: false };
    return { bg: 'bg-red-500 dark:bg-red-600', text: 'text-white', isGood: false };
  }
};

const InsightMatrix: React.FC<InsightMatrixProps> = ({
  pleiData,
  crdsData,
  oeeiData,
  ualiData,
  ilriData,
  rosrData,
}) => {



  const renderMetricAccordionItem = (metricData: MetricData, index: number) => {
    const { score, name, title, definition, contributingIssues = [] } = metricData;
    const classes = getSeverityClasses(score, name);
    const scoreDisplay = `${score.toFixed(0)}%`;
    const hasContributingIssues = contributingIssues.length > 0;

    return (
      <AccordionItem key={index} value={`metric-${index}`} className="border border-gray-200 dark:border-gray-700 rounded-lg mb-4 overflow-hidden">
        <AccordionTrigger className={`${classes.bg} ${classes.text} hover:no-underline px-6 py-4`}>
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <div>
                <h3 className="text-lg font-semibold">{title} ({name})</h3>
                <p className={`text-sm ${classes.text} opacity-90`}>{definition}</p>
              </div>
            </div>
            <div className="text-3xl font-bold">{scoreDisplay}</div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="bg-white dark:bg-gray-800 px-6 py-4">
          {hasContributingIssues ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">{contributingIssues.length} Contributing Issue{contributingIssues.length > 1 ? 's' : ''}</h4>
              </div>
              <div className="space-y-3">
                {contributingIssues.map((issue, issueIndex) => (
                  <div key={`${issue.id}-${issueIndex}`} className="bg-gray-50 dark:bg-gray-700 rounded-md p-4 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-sm text-gray-900 dark:text-gray-100 flex-1">{issue.description}</div>
                      <div className="flex items-center gap-2 ml-2">
                        {issue.savings !== undefined && (<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">{issue.savings.toFixed(1)}{issue.numericUnit || ''}</span>)}
                      </div>
                    </div>
                    {issue.details && (<div className="text-xs text-gray-600 dark:text-gray-400 mb-2 italic">Impact: {issue.details}</div>)}
                    {issue.affectedResources && issue.affectedResources.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">AFFECTED RESOURCES:</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                          {issue.affectedResources.slice(0, 3).map((resource, resIndex) => (
                            <div key={`${resource.url}-${resIndex}`} className="flex items-center gap-2 truncate">
                              <span className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0"></span>
                              <span className="truncate">{resource.url}</span>
                              {resource.wastedMs && (<span className="text-orange-600 dark:text-orange-400 font-medium">{resource.wastedMs.toFixed(0)}ms</span>)}
                              {resource.totalBytes && !resource.wastedMs && (<span className="text-purple-600 dark:text-purple-400 font-medium">{(resource.totalBytes / 1024).toFixed(1)}KB</span>)}
                            </div>
                          ))}
                          {issue.affectedResources.length > 3 && (<div className="text-xs italic text-gray-500">... and {issue.affectedResources.length - 3} more</div>)}
                        </div>
                      </div>
                    )}
                    {issue.effort !== undefined && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-500">Effort:</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">{issue.effort}/3</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-green-600 dark:text-green-400 font-medium">✓ This metric is performing well</div>
          )}
        </AccordionContent>
      </AccordionItem>
    );
  };

  const allMetrics = [pleiData, crdsData, oeeiData, ualiData, ilriData, rosrData].filter((metric): metric is MetricData => metric !== undefined);

  if (allMetrics.length === 0) {
    return (<div className="p-6 text-center text-gray-500">No metric data available for this category.</div>);
  }

  return (
    <div className="space-y-4">
      <Accordion type="multiple" className="space-y-4">
        {allMetrics.map((metric, index) => renderMetricAccordionItem(metric, index))}
      </Accordion>
    </div>
  );
};

export default InsightMatrix;
